import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, Circle, Sparkles, ChevronDown, Send, Loader2,
} from "lucide-react";

const CHECKLIST_ITEMS = [
  // Core verification
  { key: "deal_available", label: "Deal is still available", category: "core" },
  { key: "value_matches", label: "Value matches the listing", category: "price" },
  { key: "freebie_quality", label: "Deal quality matched expectations", category: "price" },
  // Terms & conditions
  { key: "terms_accurate", label: "Terms & conditions are accurate", category: "terms" },
  { key: "birthday_window", label: "Birthday window / dates are correct", category: "terms" },
  { key: "purchase_req", label: "Purchase requirement is as described", category: "terms" },
  { key: "signup_required", label: "Signup / loyalty program requirement accurate", category: "terms" },
  { key: "id_required", label: "ID or proof of birthday requirement is correct", category: "terms" },
  // Ease & experience
  { key: "easy_to_redeem", label: "Was easy to redeem", category: "ease" },
  { key: "wait_reasonable", label: "Wait time was reasonable", category: "ease" },
  { key: "offer_link_works", label: "Offer link / website is working", category: "ease" },
  // Location & staff
  { key: "location_active", label: "Location was open and active", category: "location" },
  { key: "hours_accurate", label: "Business hours were accurate", category: "location" },
  { key: "parking_available", label: "Parking was available", category: "location" },
  { key: "staff_aware", label: "Staff knew about the promo", category: "social" },
  // Social
  { key: "would_recommend", label: "Would recommend to a friend", category: "social" },
];

const CATEGORY_COLORS = {
  core: "text-emerald-600",
  price: "text-green-600",
  terms: "text-amber-600",
  ease: "text-sky-600",
  social: "text-violet-600",
  location: "text-teal-600",
};

export default function InlineVerificationChecklist({
  steps,
  onToggle,
  onSubmit,
  userHasVerified,
  submitting = false,
  accentColor = "#3b82f6",
}) {
  const [expanded, setExpanded] = useState(!userHasVerified);

  const checkedCount = CHECKLIST_ITEMS.filter((s) => steps[s.key]).length;
  const total = CHECKLIST_ITEMS.length;
  const progressPct = Math.round((checkedCount / total) * 100);
  const isComplete = progressPct === 100;
  const canSubmit = checkedCount >= 1;

  // Points: base 15, +1 per extra check beyond 3
  const bonusPts = Math.max(0, checkedCount - 3);
  const totalPts = checkedCount >= 1 ? 15 + bonusPts : 0;

  // Auto-determine verdict based on checks
  const autoVerdict = progressPct >= 60 ? "confirmed" : progressPct >= 30 ? "changed" : "denied";

  if (userHasVerified) {
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 p-4"
        data-testid="verification-completed-card"
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          <span className="text-sm font-bold text-emerald-700">
            You've verified this deal
          </span>
        </div>
        <p className="text-xs text-emerald-600 mt-1 ml-7">
          Thank you for helping the community!
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm"
      data-testid="inline-verification-checklist"
    >
      {/* Header — collapsible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid="verification-checklist-toggle"
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Sparkles className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800">
            Verification Checklist
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {checkedCount === 0
              ? "Check what you can confirm, then submit"
              : `${checkedCount}/${total} checked · +${totalPts} pts`}
          </p>
        </div>
        {/* Mini progress ring */}
        <div className="relative w-9 h-9 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15" fill="none"
              stroke={isComplete ? "#10b981" : accentColor}
              strokeWidth="3"
              strokeDasharray={`${(progressPct / 100) * 94.25} 94.25`}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-slate-600">
            {progressPct}%
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Expanded checklist + submit */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 space-y-1.5" data-testid="verification-steps">
              {CHECKLIST_ITEMS.map((item, i) => {
                const checked = !!steps[item.key];
                return (
                  <motion.button
                    key={item.key}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    onClick={() => onToggle(item.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                      checked
                        ? "bg-emerald-50 border-emerald-200 shadow-sm"
                        : "bg-white border-slate-100 hover:border-slate-300"
                    }`}
                    data-testid={`vc-step-${item.key}`}
                  >
                    {checked ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 15 }}
                      >
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      </motion.div>
                    ) : (
                      <Circle className={`w-5 h-5 flex-shrink-0 ${CATEGORY_COLORS[item.category] || "text-slate-300"}`} />
                    )}
                    <span className={`text-sm ${checked ? "text-emerald-800" : "text-slate-700"}`}>
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            {/* Points earned + Submit */}
            <div className="px-4 pb-4 space-y-2.5">
              {checkedCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl"
                  data-testid="verification-points-preview"
                >
                  <span className="text-xs font-medium text-amber-700">
                    Points earned
                  </span>
                  <span className="text-sm font-bold text-amber-600">
                    +{totalPts} pts
                  </span>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => canSubmit && onSubmit(autoVerdict)}
                disabled={!canSubmit || submitting}
                className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm ${
                  canSubmit
                    ? isComplete
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200"
                      : "bg-sky-500 hover:bg-sky-600 text-white shadow-sky-200"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
                data-testid="verification-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {submitting
                  ? "Submitting..."
                  : canSubmit
                    ? `Submit Verification (+${totalPts} pts)`
                    : "Check at least 1 item to submit"}
              </motion.button>

              {canSubmit && (
                <p className="text-[10px] text-center text-slate-400">
                  {checkedCount < 5
                    ? `${total - checkedCount} more items to check for bonus points`
                    : isComplete
                      ? "Perfect verification! Maximum points earned"
                      : `${total - checkedCount} items unchecked — we'll look into those`}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { CHECKLIST_ITEMS };
